import { useSessionStore } from '@/domains/-session/stores/useSessionStore';
import { i18n } from '@/i18n';
import router from '@/router';
import axios, {
  AxiosHeaders,
  AxiosInterceptorManager,
  AxiosInterceptorOptions,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { useToast } from 'vue-toastification';

export type Response<Data> = Promise<AxiosResponse<Data>>;

export type WorkspaceId = number;
export type PersonId = string;
export type OrganizationId = string;
export type PromotionId = string;

export type Email = string;
export type DateString = string;
export type Version = string;

export type Lang = 'fr' | 'en' | 'es';
export type PersonRole = 'admin' | 'coach' | 'extern' | 'coachee';

export namespace Template {
  export interface Params {}

  export interface Result {}
}

export namespace GetWorkspaceCoachee {
  export interface Result {
    public_id: string;
    email: string;
    lang?: Lang;
    is_verified: boolean;
    workspace_id: WorkspaceId;
    person_alias: GetPersonAlias;
    person_roles: (GetRoleStaff)[];
  }
}

export interface GetRoleStaff {
  person_role: Exclude<PersonRole, 'coachee'>;
  workspace_id: WorkspaceId;
  created_at: DateString;
  updated_at?: DateString;
}

export interface GetPersonAlias {
  first_name: string;
  last_name: string;
  created_at: DateString;
  updated_at?: DateString;
}

export namespace GetMe {
  export interface Result {
    public_id: PersonId;
    first_name: string;
    last_name: string;
    email: Email;
    lang?: Lang;
    is_verified: boolean;
    lang_was_picked: boolean;
    alias?: {
      first_name: string;
      last_name: string;
      created_at: DateString;
      updated_at?: DateString;
    };
    roles: GetRoleStaff[];
  }
}

export namespace GetWorkspaceExtern {
  export interface Result {
    public_id: PersonId;
    person_alias: GetPersonAlias;
    person_roles: GetRoleStaff[];
  }
}
export namespace GetWorkspaceExterns {
  export type Result = GetWorkspaceExtern.Result[];
}

export namespace GetWorkspaceStaff {
  export interface Result {
    public_id: PersonId;
    email: string;
    is_verified: boolean;
    person_alias: GetPersonAlias;
    person_roles: GetRoleStaff[];
  }
}
export namespace GetWorkspaceStaffs {
  export type Result = GetWorkspaceStaff.Result[];
}

export namespace GetWorkspace {
  // Returns the workspace from the point of view of the current user.
  export interface Result {
    id: WorkspaceId;
    name: string;
    config: {
      allow_promotion: boolean;
    };
    allow_meeting_coach_selection: boolean;
    allow_automatic_email: boolean;
    roles: PersonRole[]; // user <-> workspace
  }

  export type ResultWithCredits = Result & {};
}

export interface HttpClient {
  post(url: string, body?: any): Promise<any>;

  get(url: string, config?: AxiosRequestConfig): Promise<any>;

  put(url: string, body?: any): Promise<any>;

  patch(url: string, body?: any): Promise<any>;

  delete(url: string, body?: any): Promise<any>;

  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse>;
  };
}

export class SDK {
  constructor(private api: HttpClient) {
    api.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (401 === error.response.status) {
          console.log('DISCONNECT')
          const session = useSessionStore()
          session.signOut()
          //const toast = useToast();
          //toast.error(i18n.global.t('errors.disconnected'));
          router.replace({ name: 'disconnected' });
        } else {
          return Promise.reject(error);
        }
      },
    );
  }

  getMe(): Response<GetMe.Result> {
    return this.api.get(`/me`);
  }

  getAnalysisData(analysisId: string): Response<any> {
    return this.api.get(`/analysis-group/${analysisId}`);
  }

  // EXTERNS
  getWorkspaceExterns(workspaceId: WorkspaceId): Response<GetWorkspaceExterns.Result> {
    return this.api.get(`/workspace/${workspaceId}/externs`);
  }
}

export const sdk = new SDK(
  axios.create({
    withCredentials: true,
    baseURL: import.meta.env.VITE_API_URL,
    headers: new axios.AxiosHeaders({
      'Omind-Application': 'hub.omind',
    }),
  }),
);

export default sdk;
